import * as S from './styles.ts';
import { useTypedDispatch, useTypedSelector } from '@/redux/helpers/hooks.ts';
import { useEffect, useMemo, useState } from 'react';
import { getAreaCodes } from '@/redux/slices/numbers/thunks/getAreaCodes.ts';
import { changeNumberOption } from '@/redux/slices/numbers';
import { Form } from '@template/components';

const numberTypeOptions = {
  77: [
    { key: 'local', label: 'Geographic (Local)' },
    { key: 'wide', label: 'Non-Geographic (UK Wide)' },
    { key: 'mobile', label: 'Mobile' },
  ],
  233: [
    {
      key: 'local',
      label: 'Geographic (Local)',
    },
    {
      key: 'wide',
      label: 'Toll Free (USA Wide)',
    },
  ],
  other: [
    {
      key: 'local',
      label: 'Geographic (Local)',
    },
  ],
};

const usTollFreeOptions = [
  {
    key: '833',
    label: '833 Toll Free',
  },
  {
    key: '844',
    label: '844 Toll Free',
  },
  {
    key: '888',
    label: '888 Toll Free',
  },
];

const ukNonGeoOptions = [
  {
    key: '330',
    label: 'UK Generic (0330)',
  },
];

const ukMobileOptions = [
  {
    key: '7',
    label: 'UK Mobile (07)',
  },
];

const StandardOptions = () => {
  const dispatch = useTypedDispatch();

  const countries = useTypedSelector((state) => state.numbers.countries);
  const options = useTypedSelector((state) => state.numbers.options);

  const countryCode = options.countryCode;
  const setCountryCode = (val: string) =>
    dispatch(changeNumberOption({ key: 'countryCode', value: val }));

  const numberType = options.numberType;
  const setNumberType = (val: string) =>
    dispatch(changeNumberOption({ key: 'numberType', value: val }));

  const areaCode = options.areaCode;
  const setAreaCode = (val: string) =>
    dispatch(changeNumberOption({ key: 'areaCode', value: val }));

  const [mounted, setMounted] = useState(false);

  const selectedCountryDetails = countries?.find(
    (country) => country.value === countryCode
  );
  const availableAreaCodes = selectedCountryDetails?.area_codes;
  const availableNumberTypes =
    numberTypeOptions[countryCode] || numberTypeOptions.other;

  const areaCodes = useMemo(() => {
    switch (numberType) {
      case 'wide':
        if (countryCode === '233') {
          return usTollFreeOptions;
        } else if (countryCode === '77') {
          return ukNonGeoOptions;
        }
        break;
      case 'mobile':
        return ukMobileOptions;
    }

    return availableAreaCodes?.map((aC) => ({
      key: aC.area_code,
      label: aC.name,
    }));
  }, [countryCode, numberType, availableAreaCodes]);

  useEffect(() => {
    if (selectedCountryDetails?.value) {
      dispatch(getAreaCodes(selectedCountryDetails.value));
    }
  }, [selectedCountryDetails?.value]);

  useEffect(() => {
    if (areaCodes?.length) {
      if (mounted) {
        const defaultCodeInSelection = areaCodes.find(
          (aC) => aC.key === selectedCountryDetails?.defaultArea
        );
        setAreaCode(
          (defaultCodeInSelection
            ? selectedCountryDetails?.defaultArea
            : areaCodes[0]?.key) || ''
        );
      } else {
        setMounted(true);
      }
    }
  }, [areaCodes?.length]);

  useEffect(() => {
    if (availableNumberTypes.some((t) => t.key === numberType)) return;
    setNumberType(availableNumberTypes[0]?.key);
  }, [availableNumberTypes.length]);

  return (
    <S.StandardOptionsContainer>
      <Form
        simple
        value={{
          country: countryCode,
          numberType: numberType,
          areaCode: areaCode,
        }}
        onChange={(obj, i) => {
          const countryVal = obj.country;
          const numberTypeVal = obj.numberType;
          const areaCodeVal = obj.areaCode;
          setCountryCode(countryVal);
          setNumberType(numberTypeVal);
          setAreaCode(areaCodeVal);
        }}
        inputs={[
          {
            name: 'country',
            fullWidth: true,
            label: 'Country',
            options: countries.map((country) => ({
              key: country.value,
              label: country.name,
            })),
            search: true,
            span: 4,
            type: 'select',
            placeholder: 'Choose country',
          },
          {
            name: 'numberType',
            fullWidth: true,
            label: 'Number Type',
            options: availableNumberTypes,
            search: true,
            span: 4,
            type: 'select',
            placeholder: 'Choose number type',
          },
          {
            name: 'areaCode',
            fullWidth: true,
            label: 'Area Code',
            options: areaCodes,
            loading: availableAreaCodes?.length === 0,
            search: true,
            span: 4,
            type: 'select',
            placeholder: 'Enter location or area code',
          },
        ]}
      />
    </S.StandardOptionsContainer>
  );
};

export default StandardOptions;
